/**
 * `/time`: your own timesheet. Board (default), Day and Week views over the
 * week holding the selected day, with earlier unreleased days carried over
 * on top of today's Board. Only you see unreleased entries.
 */
import { Check, ChevronLeft, ChevronRight, Clock, Plus } from "lucide-react";
import { useMemo, useState, type FC } from "react";
import { useSearchParams } from "react-router-dom";

import { SettingsModal } from "@/components/settings/SettingsModal";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useOnScreen } from "@/features/agents/onScreen";

import { timeActions } from "../actions";
import { BoardView } from "../board/BoardView";
import { TimerButton } from "../components/TimerButton";
import { WeekStrip } from "../components/WeekStrip";
import { DayView } from "../day/DayView";
import {
  boardGroups,
  dayStats,
  daySummary,
  longDate,
  releasable,
  weekDates
} from "../model";
import {
  useDaySignals,
  useMyTimeSettings,
  useTimeEntries,
  useTimeStatus,
  useTimeToday
} from "../queries";
import { isTimesheetView, type TimesheetView } from "../routes";
import {
  formatHours,
  formatMoney,
  shiftDate,
  todayIso,
  weekStart
} from "../utils";
import { WeekTable } from "../week/WeekTable";

const VIEWS: { id: TimesheetView; label: string }[] = [
  { id: "board", label: "Board" },
  { id: "day", label: "Day" },
  { id: "week", label: "Week" }
];

const Stat: FC<{ label: string; value: string }> = ({ label, value }) => (
  <span>
    {label} <span className="font-medium text-fg tabular-nums">{value}</span>
  </span>
);

export const TimeOff: FC<{ isAdmin: boolean }> = ({ isAdmin }) => (
  <EmptyState
    icon={Clock}
    title="Time Tracking Is Off"
    description={
      isAdmin
        ? "Turn it on for your firm in Settings, under Time & billing. Nothing is captured until you do."
        : "Your firm hasn't turned on time tracking. Ask a firm admin if you'd like to use it."
    }
    className="h-full"
    data-testid="time-off"
  />
);

export const TimesheetPage: FC = () => {
  const status = useTimeStatus();
  const [params, setParams] = useSearchParams();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const todayQuery = useTimeToday(status.enabled);
  const settings = useMyTimeSettings(status.enabled);
  const today = todayQuery.data?.today ?? todayIso();
  const view: TimesheetView = isTimesheetView(params.get("view"))
    ? (params.get("view") as TimesheetView)
    : "board";
  const date = params.get("date") ?? today;
  const focusId = params.get("entry");

  const dates = useMemo(() => weekDates(weekStart(date)), [date]);
  const carryDays = useMemo(
    () => todayQuery.data?.earlier_unreleased ?? [],
    [todayQuery.data]
  );
  const from =
    carryDays.length && carryDays[0].work_date < dates[0]
      ? carryDays[0].work_date
      : dates[0];
  const entriesQuery = useTimeEntries(from, dates[6], status.enabled);
  const signals = useDaySignals(date, status.enabled && view === "day");
  const entries = useMemo(
    () => entriesQuery.data?.entries ?? [],
    [entriesQuery.data]
  );

  const focused = focusId ? entries.find((e) => e.id === focusId) : undefined;
  useOnScreen(
    focused
      ? {
          kind: "time_entry",
          id: focused.id,
          label: `${formatHours(focused.duration_hours)}, ${
            focused.case_label || focused.client_label
          }`,
          date: focused.work_date
        }
      : null
  );

  const groups = useMemo(
    () => boardGroups(entries, date, today, carryDays),
    [entries, date, today, carryDays]
  );
  const stats = useMemo(() => dayStats(entries, dates), [entries, dates]);
  const summary = daySummary(entries, date);
  const ready = releasable(groups);

  const go = (next: {
    view?: TimesheetView;
    date?: string;
    entry?: string | null;
  }) => {
    const p = new URLSearchParams(params);
    const v = next.view ?? view;
    if (v === "board") p.delete("view");
    else p.set("view", v);
    const d = next.date ?? date;
    if (d === today) p.delete("date");
    else p.set("date", d);
    if (next.entry === null || next.date || next.view) p.delete("entry");
    if (next.entry) p.set("entry", next.entry);
    setParams(p, { replace: true });
  };
  const step = view === "week" ? 7 : 1;
  const nextDate = shiftDate(date, step);

  if (!status.isLoading && !status.enabled)
    return <TimeOff isAdmin={status.is_admin} />;

  const reviewTime = settings.data?.evening_review_time ?? "18:00";
  const label =
    view === "week"
      ? `Week of ${longDate(dates[0]).split(", ")[1]}`
      : longDate(date);

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface"
      data-testid="timesheet-page"
    >
      <div className="flex h-14 shrink-0 items-center gap-3 px-6">
        <h1 className="text-xl font-medium text-fg">Time</h1>
        <span className="inline-flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={view === "week" ? "Previous week" : "Previous day"}
            onClick={() => go({ date: shiftDate(date, -step) })}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="secondary"
            size="xs"
            onClick={() => go({ date: today })}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={view === "week" ? "Next week" : "Next day"}
            disabled={
              nextDate > today &&
              (view !== "week" || weekStart(nextDate) > today)
            }
            onClick={() => go({ date: nextDate > today ? today : nextDate })}
          >
            <ChevronRight />
          </Button>
        </span>
        <span
          className="text-base font-medium whitespace-nowrap text-fg"
          data-testid="timesheet-date"
        >
          {label}
        </span>
        <Tabs
          value={view}
          onValueChange={(v) => go({ view: v as TimesheetView })}
        >
          <TabsList aria-label="View">
            {VIEWS.map((v) => (
              <TabsTrigger key={v.id} value={v.id}>
                {v.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <span className="flex-1" />
        <span className="mr-1.5 hidden items-center gap-1.5 text-sm whitespace-nowrap text-fg-3 xl:inline-flex">
          <Stat label="Captured" value={formatHours(summary.captured)} />
          <span aria-hidden>·</span>
          <Stat label="Released" value={formatHours(summary.released)} />
          <span aria-hidden>·</span>
          <Stat label="Hourly Value" value={formatMoney(summary.value)} />
        </span>
        <TimerButton timer={todayQuery.data?.timer ?? null} />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            const p = new URLSearchParams(params);
            p.set("view", "day");
            p.delete("date");
            p.delete("entry");
            p.set("new", "1");
            setParams(p, { replace: true });
          }}
        >
          <Plus /> Add Time
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={!ready.length}
          onClick={() => void timeActions.release(ready)}
          data-testid="release-all"
        >
          <Check /> Release All {ready.length}
        </Button>
      </div>

      <WeekStrip
        dates={dates}
        today={today}
        selected={date}
        stats={stats}
        onSelect={(d) => go({ date: d, view: view === "week" ? "day" : view })}
      />

      {status.isLoading || entriesQuery.isPending ? (
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-28 w-full max-w-3xl" />
          <Skeleton className="h-28 w-full max-w-3xl" />
        </div>
      ) : view === "week" ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <WeekTable entries={entries} dates={dates} today={today} />
        </div>
      ) : view === "day" ? (
        <DayView
          key={date}
          date={date}
          today={today}
          entries={entries.filter((e) => e.work_date === date)}
          signals={signals.data ?? []}
          focusId={focusId}
          openNew={params.get("new") === "1"}
          onOpenedNew={() => {
            const p = new URLSearchParams(params);
            p.delete("new");
            setParams(p, { replace: true });
          }}
        />
      ) : (
        <BoardView
          groups={groups}
          today={today}
          date={date}
          reviewTime={reviewTime}
          focusId={focusId}
        />
      )}

      {settings.data &&
      !settings.data.email_capture &&
      !settings.data.calendar_capture ? (
        <footer className="flex h-8 shrink-0 items-center gap-2 border-t border-line-2 px-6 text-sm text-fg-3">
          <Clock className="size-3.5" />
          <span className="truncate">
            Time in CaseBlink is captured automatically. Add sent email and
            meetings in{" "}
            <button
              type="button"
              className="font-medium text-fg hover:underline"
              onClick={() => setSettingsOpen(true)}
            >
              Time Capture Settings
            </button>
            .
          </span>
        </footer>
      ) : null}
      {settingsOpen ? (
        <SettingsModal
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          initialSection="time-capture"
        />
      ) : null}
    </div>
  );
};

export default TimesheetPage;
